import type { ReactNode } from 'react';

interface WizardLayoutProps {
  currentStep: number;
  /** MUL-3919: total may be 8 or 9 depending on eligibility. */
  totalSteps: number;
  /** Title for the current step; drives the indicator copy and a11y label. */
  stepTitle: string;
  children: ReactNode;
}

// Bundled with the app, not pulled from Webflow's CDN. The company is off Webflow;
// an external host we no longer pay for is one lapsed invoice away from a
// broken logo on a customer-facing page.
const LOGO_URL = '/hts-logo-white.png';

export function WizardLayout({ currentStep, totalSteps, stepTitle, children }: WizardLayoutProps) {
  return (
    <div className="app-container">
      <header className="app-header">
        <img src={LOGO_URL} alt="Harris Technology Services" />
        <h1>Subcontractor Onboarding</h1>
      </header>

      <main className="app-main">
        {/* Step indicator */}
        <div className="step-indicator">
          <span className="step-indicator-label" aria-current="step">
            Step {currentStep + 1} of {totalSteps}
          </span>
          <span className="step-indicator-title">{stepTitle}</span>
        </div>

        {/* Progress bar */}
        <div
          className="stepper"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-valuenow={currentStep + 1}
          aria-label={`Application progress: step ${currentStep + 1} of ${totalSteps}, ${stepTitle}`}
        >
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className={`stepper-step${i < currentStep ? ' completed' : ''}${i === currentStep ? ' active' : ''}`}
            />
          ))}
        </div>

        {children}
      </main>

      <footer className="app-footer">
        <p>
          &copy; {new Date().getFullYear()} Harris Technology Services &bull;{' '}
          <a href="https://hts.pro">hts.pro</a>
        </p>
        <p style={{ marginTop: 4 }}>
          {/* MUL-3919 §6.0 item 8: footer links must take their colour from
              `.app-footer a` (navy, 14.04:1 on the body bg). An inline
              `style={{ color }}` overrides that class rule — the previous
              tel: and mailto: anchors were still rendering Action Blue
              (#00B8EB) at ~2.1:1 on #F1F4F9 and failing contrast. No inline
              colour here; let the class rule apply. */}
          Questions? Call <a href="tel:+18778772508">1-877-877-2508</a> or email{' '}
          <a href="mailto:subonboarding@hts.pro">subonboarding@hts.pro</a>
        </p>
      </footer>
    </div>
  );
}